import { apiBaseUrl, apiRequest } from '../../src/services/api';
import { t } from '../../src/services/language';
import { getSettings, saveSettings } from '../../src/services/settings';
import { isSummaryTemplateId } from '../../src/services/summaryTemplates';
import type { SignInResult } from '../../src/shared/types';
import { forgetCalendarAccounts } from './calendarAccounts';
import { goToProvider } from './providerRedirect';
import { SUPPORT_EMAIL } from './shims/support';
import { getSupabase, isSupabaseConfigured } from './shims/supabaseClient';

/**
 * Web'de giriş: Google'a yönlendirme (PKCE). Masaüstündeki gibi tek onayda
 * takvim izni de isteniyor; Google'ın verdiği yenileme anahtarı dönüşte
 * sunucuya iletiliyor ki takvim ve otomatik bot sunucuda çalışsın.
 *
 * Asıl yol: Google kodu uygulamaya döndürüyor (`/baglanti/google`), sunucu onu
 * kimlik belirtecine çeviriyor, oturum `signInWithIdToken` ile açılıyor. Böylece
 * Google'ın hesap seçme ekranında Supabase'in değil uygulamanın alan adı görünüyor.
 * Sunucuda Google istemcisi tanımlı değilse Supabase yönlendirmesine dönülüyor.
 */

/** Takvim kapsamı; sunucudaki `GOOGLE_SCOPES` ile aynı (web/lib/calendar/google.ts). */
const CALENDAR_SCOPES = 'https://www.googleapis.com/auth/calendar.readonly';
const PENDING_KEY = 'talknote-giris';
/** Doğrudan giriş bu sekmede tamamlanamadıysa sonraki deneme Supabase yönlendirmesiyle. */
const FALLBACK_KEY = 'talknote-giris-yedek';
const RETURN_PATH = /^\/baglanti\/google\/?$/;

interface PendingSignIn {
  state: string;
  nonce: string;
  verifier: string;
  returnTo: string;
}

/** Dönüşteki kod oturuma çevrilirken oturum durumu sorulursa önce o bitsin. */
let signInReturn: Promise<void> | null = null;
/** Bu girişte Google'ın verdiği yenileme anahtarı — oturum açılınca sunucuya iletilir. */
let calendarGrant: string | null = null;

function base64Url(data: Uint8Array): string {
  return btoa(String.fromCharCode(...data)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomToken(bytes = 32): string {
  return base64Url(crypto.getRandomValues(new Uint8Array(bytes)));
}

async function sha256(value: string): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value)));
}

function hex(data: Uint8Array): string {
  return Array.from(data, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/** Oturum açılmadan çağrılan uçlar için (apiRequest oturum istiyor). */
async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${apiBaseUrl()}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal: AbortSignal.timeout(20_000),
  });
  const json = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
  return json;
}

/** Google'dan hatayla dönüşte giriş ekranında bir kez gösterilecek mesaj. */
let signInError: string | null = null;

function describeSignInError(raw: string): string {
  if (/beta_invite_required/i.test(raw)) {
    return t(
      `TalkNote şu an kapalı beta aşamasında ve bu Google hesabı davet listesinde değil. Davet için ${SUPPORT_EMAIL} adresine yazabilirsiniz.`,
      `TalkNote is in closed beta and this Google account is not on the invite list. To request an invite, write to ${SUPPORT_EMAIL}.`
    );
  }
  if (/banned/i.test(raw)) {
    return t(
      `Hesabın askıya alındı. Ayrıntı için ${SUPPORT_EMAIL} adresine yazabilirsin.`,
      `Your account has been suspended. For details, write to ${SUPPORT_EMAIL}.`
    );
  }
  if (/access_denied/i.test(raw)) {
    return t('Google izni verilmedi. Giriş için izin vermelisin.', 'Google permission was denied. You need to allow access to sign in.');
  }
  return t('Giriş yapılamadı. Tekrar dene.', 'Could not sign in. Please try again.');
}

function stripParams(names: string[]): void {
  const url = new URL(window.location.href);
  let changed = false;
  for (const name of names) {
    if (url.searchParams.has(name)) {
      url.searchParams.delete(name);
      changed = true;
    }
  }
  // Supabase hata bilgisini bazen adres parçasında (#) döndürüyor.
  if (/(^|&)error/.test(url.hash.slice(1))) {
    url.hash = '';
    changed = true;
  }
  if (changed) window.history.replaceState(window.history.state, '', url.toString());
}

/**
 * Açılışta, arayüz çizilmeden önce: Google girişinden hatayla dönüldüyse
 * mesajı saklar ve adres çubuğundan siler. `code` burada silinmez — Supabase
 * onu oturuma çeviriyor (bkz. finishSignInRedirect). Takvim bağlamadan dönüş
 * ayrı (calendarAccounts.ts > captureCalendarReturn).
 */
export function captureReturnParams(): void {
  const url = new URL(window.location.href);
  const hash = new URLSearchParams(url.hash.slice(1));
  const error = url.searchParams.get('error_description') || url.searchParams.get('error') || hash.get('error_description') || hash.get('error');
  if (error) signInError = describeSignInError(error);
  stripParams(['error', 'error_code', 'error_description']);
}

export function takeSignInError(): string | null {
  const value = signInError;
  signInError = null;
  return value;
}

function readPending(): PendingSignIn | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    const value = raw ? (JSON.parse(raw) as Partial<PendingSignIn>) : null;
    if (!value || typeof value.state !== 'string' || typeof value.nonce !== 'string' || typeof value.verifier !== 'string') return null;
    const returnTo = typeof value.returnTo === 'string' && /^\/(?!\/)/.test(value.returnTo) ? value.returnTo : '/';
    return { state: value.state, nonce: value.nonce, verifier: value.verifier, returnTo };
  } catch {
    return null;
  }
}

/**
 * Açılışta, arayüz çizilmeden ve takvim dönüşüne bakılmadan önce: Google
 * girişinden dönüş sayfasıysa kodu oturuma çevirmeye başlar ve adres çubuğunu
 * girişin başladığı sayfaya çevirir. Takvim bağlama da aynı adrese dönüyor;
 * hangisi olduğunu bu sekmede saklanan `state` belirliyor.
 */
export function captureSignInReturn(): void {
  if (!RETURN_PATH.test(window.location.pathname)) return;
  const params = new URLSearchParams(window.location.search);
  const saved = readPending();
  if (!saved || saved.state !== params.get('state')) return;
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    // yoksay
  }
  window.history.replaceState(null, '', saved.returnTo);

  const code = params.get('code');
  const error = params.get('error');
  if (error || !code) {
    signInError = describeSignInError(error ?? '');
    return;
  }
  signInReturn = (async () => {
    try {
      const tokens = await postJson<{ idToken: string; accessToken: string; refreshToken: string | null }>('/api/v1/auth/google/exchange', {
        code,
        codeVerifier: saved.verifier,
      });
      const { error: authError } = await getSupabase().auth.signInWithIdToken({
        provider: 'google',
        token: tokens.idToken,
        access_token: tokens.accessToken,
        nonce: saved.nonce,
      });
      if (authError) throw authError;
      calendarGrant = tokens.refreshToken;
    } catch (err) {
      const message = (err as Error).message;
      console.error('[auth] Google girişi tamamlanamadı:', message);
      signInError = describeSignInError(message);
      // Kullanıcıya özgü bir ret değilse (davet, askı) sorun bu yolda olabilir.
      if (!/beta_invite_required|banned/i.test(message)) {
        try {
          sessionStorage.setItem(FALLBACK_KEY, '1');
        } catch {
          // yoksay
        }
      }
    } finally {
      signInReturn = null;
    }
  })();
}

/** Dönüşteki giriş sürüyorsa bitmesini bekler (bkz. bridge.ts > getAuthState). */
export async function awaitSignInReturn(): Promise<void> {
  if (signInReturn) await signInReturn;
}

/**
 * Asıl yol: Google uygulamaya döner. Google'ın onay adresini verir; sunucuda
 * istemci tanımlı değilse `null`.
 */
async function startDirectSignIn(): Promise<string | null> {
  if (!apiBaseUrl()) return null;
  try {
    if (sessionStorage.getItem(FALLBACK_KEY)) return null;
  } catch {
    return null;
  }
  const pending: PendingSignIn = {
    state: randomToken(),
    nonce: randomToken(),
    verifier: randomToken(48),
    // Giriş sonrası aynı sayfaya (ör. paylaşılan bir toplantı bağlantısı) dönülsün.
    returnTo: `${window.location.pathname}${window.location.search}`,
  };
  let url: string;
  try {
    ({ url } = await postJson<{ url: string }>('/api/v1/auth/google/start', {
      state: pending.state,
      // Supabase, kimlik belirtecindeki nonce'u verilen değerin SHA-256'sı (hex) ile karşılaştırıyor.
      nonce: hex(await sha256(pending.nonce)),
      codeChallenge: base64Url(await sha256(pending.verifier)),
    }));
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  } catch (err) {
    console.warn('[auth] doğrudan Google girişi başlatılamadı, Supabase yönlendirmesi deneniyor:', (err as Error).message);
    return null;
  }
  return url;
}

export async function signInWithGoogleRedirect(): Promise<SignInResult> {
  if (!isSupabaseConfigured()) {
    return { ok: false, error: t('Uygulama yapılandırılmamış (Supabase).', 'The app is not configured (Supabase).') };
  }
  let url = await startDirectSignIn();

  if (!url) {
    // Giriş sonrası aynı sayfaya (ör. paylaşılan bir toplantı bağlantısı) dönülsün.
    const redirectTo = `${window.location.origin}${window.location.pathname}`;
    const { data, error } = await getSupabase().auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo,
        scopes: CALENDAR_SCOPES,
        // Takvimin sunucuda okunabilmesi için yenileme anahtarı.
        queryParams: { access_type: 'offline', include_granted_scopes: 'true' },
        skipBrowserRedirect: true,
      },
    });
    if (error || !data.url) {
      console.error('[auth] Google yönlendirmesi başlatılamadı:', error?.message);
      return { ok: false, error: t('Google girişi başlatılamadı.', 'Could not start Google sign-in.') };
    }
    url = data.url;
  }

  // Web'de sayfa Google'a gidiyor, buradan dönülmüyor. Mobilde tarayıcı girişsiz kapandı.
  await goToProvider(url);
  return { ok: false, error: t('Google girişi tamamlanmadı.', 'Google sign-in was not completed.') };
}

/** Supabase dönüşteki `code`'u oturuma çevirdikten sonra adres çubuğunu temizler. */
export function finishSignInRedirect(): void {
  stripParams(['code', 'state']);
}

/**
 * Girişten hemen sonra Google'ın verdiği yenileme anahtarı elde duruyor
 * (yalnızca bu dönüşte). Takvim sunucuda okunsun diye bir kez sunucuya
 * iletilir. Kullanıcı takvim iznini vermediyse sunucu reddeder; takvim
 * sonradan Ayarlar'dan bağlanabilir.
 */
export async function forwardGoogleCalendarGrant(): Promise<void> {
  const { data } = await getSupabase().auth.getSession();
  const refreshToken = calendarGrant ?? data.session?.provider_refresh_token;
  calendarGrant = null;
  if (!refreshToken) return;
  const marker = `talknote-takvim-${refreshToken.slice(-12)}`;
  try {
    if (sessionStorage.getItem(marker)) return;
    sessionStorage.setItem(marker, '1');
  } catch {
    // sessionStorage yoksa yine de bir kez dene.
  }
  try {
    await apiRequest('POST', '/api/v1/calendar/google/token', { body: { refreshToken } });
    forgetCalendarAccounts();
  } catch (err) {
    console.warn('[auth] takvim yetkisi sunucuya iletilemedi:', (err as Error).message);
  }
}

/**
 * Tercihler bu tarayıcıda ilk kez açılıyorsa sunucudaki kopyadan (masaüstünün
 * yazdığı `user_preferences`) doldurulur. Aksi hâlde yeni tarayıcının
 * varsayılanları, prefsSync ile sunucudaki gerçek tercihlerin üstüne yazardı.
 */
export async function hydratePreferences(userId: string): Promise<void> {
  const marker = `talknote-tercihler-${userId}`;
  try {
    if (localStorage.getItem(marker)) return;
  } catch {
    return;
  }
  const { data, error } = await getSupabase()
    .from('user_preferences')
    .select('ui_language, summary_language, summary_template_id, summary_rules, transcript_terms')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) {
    console.warn('[prefs] sunucu tercihleri okunamadı:', error.message);
    return;
  }
  if (data) {
    const current = await getSettings();
    await saveSettings({
      uiLanguage: data.ui_language ?? current.uiLanguage,
      summaryLanguage: data.summary_language ?? current.summaryLanguage,
      summaryStyle: {
        templateId: isSummaryTemplateId(data.summary_template_id) ? data.summary_template_id : current.summaryStyle.templateId,
        rules: Array.isArray(data.summary_rules) ? data.summary_rules : current.summaryStyle.rules,
      },
      transcriptTerms: Array.isArray(data.transcript_terms) ? data.transcript_terms : current.transcriptTerms,
    });
  }
  try {
    localStorage.setItem(marker, '1');
  } catch {
    // yoksay
  }
}
